import emailjs from '@emailjs/browser';
import { settingsService } from '../firebase/settingsService';

// EmailJS Configuration
// Template is configured in EmailJS with:
// - To Email: {{email}}
// - Subject: {{subject}}
// - Content: {{{html_content}}}  <-- TRIPLE BRACES for raw HTML
// - From Name: {{name}}
// - Reply To: {{email}}
const CONFIG = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_azjo2ta',
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '0l6yIA9JtgnkrhtbP',
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_8plz3lp'
};

class EmailService {
  constructor() {
    this.initialized = false;
    this.settings = null;
    this.invoiceSettings = null;
  }

  /**
   * Initialize EmailJS and load settings
   */
  async init() {
    if (this.initialized) return this;

    try {
      emailjs.init(CONFIG.publicKey);
      this.settings = await settingsService.getSettings();
      this.invoiceSettings = this.settings?.invoiceSettings || {};
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize email service:', error);
      // Still mark as initialized to prevent blocking
      this.initialized = true;
    }

    return this;
  }

  /**
   * Get admin email for notifications
   */
  getAdminEmail() {
    return this.settings?.email || 'info@koholistichealth.com';
  }

  /**
   * Wrap content in email template with header, footer, and styling
   * Uses the same template structure as the working newsletter emails
   */
  wrapInEmailTemplate(bodyContent, options = {}) {
    const businessName = this.settings?.businessName || 'KO Holistic Health';
    const phone = this.settings?.phone || '0439 386 551';
    const emailAddr = this.settings?.email || 'info@koholistichealth.com';
    const websiteUrl = 'https://koholistichealth.com.au';
    const currentYear = new Date().getFullYear();
    const preheader = options.preheader || '';

    return `
<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="x-apple-disable-message-reformatting">
  <meta name="format-detection" content="telephone=no,address=no,email=no,date=no,url=no">
  <title>${businessName}</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:AllowPNG/>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
  <style>
    /* Reset styles */
    body, table, td, p, a, li, blockquote { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { -ms-interpolation-mode: bicubic; border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; }
    body { margin: 0 !important; padding: 0 !important; width: 100% !important; }

    /* iOS blue links */
    a[x-apple-data-detectors] { color: inherit !important; text-decoration: none !important; font-size: inherit !important; font-family: inherit !important; font-weight: inherit !important; line-height: inherit !important; }

    /* Gmail blue links */
    u + #body a { color: inherit; text-decoration: none; font-size: inherit; font-family: inherit; font-weight: inherit; line-height: inherit; }

    /* Samsung Mail */
    #MessageViewBody a { color: inherit; text-decoration: none; font-size: inherit; font-family: inherit; font-weight: inherit; line-height: inherit; }

    /* Preheader styles */
    .preheader { display: none !important; visibility: hidden; opacity: 0; color: transparent; height: 0; width: 0; mso-hide: all; }

    /* Responsive styles */
    @media only screen and (max-width: 620px) {
      .email-container { width: 100% !important; margin: auto !important; }
      .fluid { max-width: 100% !important; height: auto !important; margin-left: auto !important; margin-right: auto !important; }
      .stack-column, .stack-column-center { display: block !important; width: 100% !important; max-width: 100% !important; direction: ltr !important; }
      .stack-column-center { text-align: center !important; }
      .mobile-center { text-align: center !important; }
      .mobile-padding { padding-left: 20px !important; padding-right: 20px !important; }
      .mobile-button { width: 100% !important; }
    }
  </style>
</head>
<body id="body" style="margin: 0; padding: 0; word-spacing: normal; background-color: #f5f5f0;">

  <!-- Preheader Text -->
  <div class="preheader" style="display: none; max-height: 0; overflow: hidden;">
    ${preheader}
    &#847; &#847; &#847; &#847; &#847; &#847; &#847; &#847; &#847; &#847; &#847; &#847; &#847; &#847; &#847;
  </div>

  <!-- Email Wrapper -->
  <table role="presentation" style="width: 100%; border: none; border-spacing: 0; background-color: #f5f5f0;">
    <tr>
      <td align="center" style="padding: 20px 10px;">

        <!-- Email Container -->
        <table role="presentation" class="email-container" style="width: 600px; max-width: 600px; border: none; border-spacing: 0; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 1px 3px rgba(0, 0, 0, 0.08);">

          <!-- Header -->
          <tr>
            <td bgcolor="#7a8a47" style="background-color: #7a8a47; padding: 32px 40px; text-align: center;">
              <table role="presentation" style="width: 100%; border: none; border-spacing: 0;">
                <tr>
                  <td style="text-align: center; padding: 0;">
                    <h1 style="margin: 0; font-family: Georgia, 'Times New Roman', serif; color: #ffffff; font-size: 28px; font-weight: 600; letter-spacing: 1px; line-height: 1.2;">
                      KO Holistic Health
                    </h1>
                    <p style="margin: 8px 0 0; font-family: Arial, Helvetica, sans-serif; color: #e8f0d8; font-size: 14px; font-weight: 400; letter-spacing: 0.5px;">
                      Your Wellness Journey
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Body Content -->
          <tr>
            <td class="mobile-padding" style="padding: 40px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 16px; line-height: 1.6; color: #333333;">
              ${bodyContent}
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #f8f7f4; padding: 32px 40px; border-top: 1px solid #e8e6e1;">
              <table role="presentation" style="width: 100%; border: none; border-spacing: 0;">
                <!-- Company Name -->
                <tr>
                  <td style="text-align: center; padding-bottom: 16px;">
                    <p style="margin: 0; font-family: 'Georgia', 'Times New Roman', serif; color: #8a9957; font-weight: 600; font-size: 18px;">
                      ${businessName}
                    </p>
                  </td>
                </tr>

                <!-- Social Links -->
                <tr>
                  <td style="text-align: center; padding-bottom: 16px;">
                    <table role="presentation" style="margin: 0 auto; border: none; border-spacing: 0;">
                      <tr>
                        <td style="padding: 0 8px;">
                          <a href="https://facebook.com/koholistichealth" style="display: inline-block; width: 36px; height: 36px; background-color: #8a9957; border-radius: 50%; text-align: center; line-height: 36px; text-decoration: none;">
                            <img src="https://cdn-icons-png.flaticon.com/512/124/124010.png" alt="Facebook" width="18" height="18" style="vertical-align: middle; filter: brightness(0) invert(1);">
                          </a>
                        </td>
                        <td style="padding: 0 8px;">
                          <a href="https://instagram.com/koholistichealth" style="display: inline-block; width: 36px; height: 36px; background-color: #8a9957; border-radius: 50%; text-align: center; line-height: 36px; text-decoration: none;">
                            <img src="https://cdn-icons-png.flaticon.com/512/174/174855.png" alt="Instagram" width="18" height="18" style="vertical-align: middle; filter: brightness(0) invert(1);">
                          </a>
                        </td>
                        <td style="padding: 0 8px;">
                          <a href="${websiteUrl}" style="display: inline-block; width: 36px; height: 36px; background-color: #8a9957; border-radius: 50%; text-align: center; line-height: 36px; text-decoration: none;">
                            <img src="https://cdn-icons-png.flaticon.com/512/1006/1006771.png" alt="Website" width="18" height="18" style="vertical-align: middle; filter: brightness(0) invert(1);">
                          </a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                <!-- Contact Info -->
                <tr>
                  <td style="text-align: center; padding-bottom: 12px;">
                    <p style="margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; color: #666666; font-size: 13px; line-height: 1.5;">
                      Sydney, NSW, Australia<br>
                      ${phone} | ${emailAddr}
                    </p>
                  </td>
                </tr>

                <!-- Website Link -->
                <tr>
                  <td style="text-align: center; padding-bottom: 16px;">
                    <a href="${websiteUrl}" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; color: #8a9957; font-size: 13px; text-decoration: underline;">
                      Visit our website
                    </a>
                  </td>
                </tr>

                <!-- Copyright -->
                <tr>
                  <td style="text-align: center;">
                    <p style="margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; color: #aaaaaa; font-size: 11px;">
                      © ${currentYear} ${businessName}. All rights reserved.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>
        <!-- End Email Container -->

      </td>
    </tr>
  </table>
  <!-- End Email Wrapper -->

</body>
</html>
    `.trim();
  }

  /**
   * Generate booking confirmation HTML content
   */
  generateBookingHTML(booking, invoice = null) {
    const customerName = booking.clientName || booking.name || 'there';
    const serviceName = booking.serviceName || booking.service || 'Your Session';
    const bookingDate = booking.dateString || booking.date || '';
    const bookingTime = booking.time || '';
    const duration = booking.duration || '';
    const isCustomTime = booking.isCustomTimeRequest;

    let content = `
      <h2 style="margin: 0 0 24px; color: #8a9957; font-size: 24px; font-weight: 600;">
        Booking Confirmation
      </h2>

      <p style="margin: 0 0 20px;">Hi ${customerName},</p>

      <p style="margin: 0 0 24px;">
        Thank you for booking with KO Holistic Health! Your booking request has been received and is pending confirmation.
      </p>

      <!-- Booking Details Box -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">Booking Details</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #666; width: 120px;"><strong>Service:</strong></td>
            <td style="padding: 8px 0; color: #333;">${serviceName}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Date:</strong></td>
            <td style="padding: 8px 0; color: #333;">${bookingDate}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Time:</strong></td>
            <td style="padding: 8px 0; color: #333;">${bookingTime}${isCustomTime ? ' (Custom Request)' : ''}</td>
          </tr>
          ${duration ? `
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Duration:</strong></td>
            <td style="padding: 8px 0; color: #333;">${duration}</td>
          </tr>
          ` : ''}
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Location:</strong></td>
            <td style="padding: 8px 0; color: #333;">Studio</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Status:</strong></td>
            <td style="padding: 8px 0;">
              <span style="display: inline-block; background-color: #fef3cd; color: #856404; padding: 4px 12px; border-radius: 4px; font-size: 14px;">
                Pending Confirmation
              </span>
            </td>
          </tr>
        </table>
      </div>
    `;

    // Add invoice section if invoice exists
    if (invoice) {
      content += this.generateInvoiceSection(invoice);
    }

    content += `
      <p style="margin: 24px 0;">
        Kirstie will review your booking and confirm shortly. You'll receive your invoice once your booking is confirmed.
      </p>

      <p style="margin: 0 0 8px;">If you have any questions, please don't hesitate to contact us.</p>

      <p style="margin: 24px 0 0;">
        Warmly,<br>
        <strong style="color: #8a9957;">Kirstie</strong><br>
        KO Holistic Health
      </p>
    `;

    return content;
  }

  /**
   * Generate invoice section HTML
   */
  generateInvoiceSection(invoice) {
    const invoiceNumber = invoice.invoiceNumber || 'N/A';
    // Check GST from stored invoice data first, fall back to settings
    const gstRegistered = invoice.gstRegistered !== undefined
      ? invoice.gstRegistered === true
      : this.invoiceSettings?.gstRegistered === true;
    const gstRate = invoice.taxRate || this.invoiceSettings?.gstRate || 10;
    const taxAmount = gstRegistered && invoice.taxAmount > 0 ? (invoice.taxAmount || 0).toFixed(2) : '0.00';

    // Use the stored total directly (already correctly calculated with/without GST)
    const total = (invoice.total || 0).toFixed(2);

    // Format due date
    let dueDateStr = 'Due on receipt';
    if (invoice.dueDate) {
      const dueDate = invoice.dueDate.toDate ? invoice.dueDate.toDate() : new Date(invoice.dueDate);
      dueDateStr = dueDate.toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }

    // Get bank details from invoice settings
    const bankName = this.invoiceSettings?.bankName || 'Commonwealth Bank';
    const bsb = this.invoiceSettings?.bsb || '';
    const accountNumber = this.invoiceSettings?.accountNumber || '';
    const accountName = this.invoiceSettings?.accountName || 'KO Holistic Health';
    const paymentInstructions = this.invoiceSettings?.paymentInstructions || '';

    // Line items
    let lineItemsHtml = '';
    if (invoice.lineItems && invoice.lineItems.length > 0) {
      invoice.lineItems.forEach(item => {
        lineItemsHtml += `
          <tr>
            <td style="padding: 12px 0; border-bottom: 1px solid #e8e6e1; color: #333;">${item.description || 'Service'}</td>
            <td style="padding: 12px 0; border-bottom: 1px solid #e8e6e1; text-align: right; color: #333;">$${(item.total || 0).toFixed(2)}</td>
          </tr>
        `;
      });
    }

    // GST row - only show if GST registered and tax amount > 0
    const gstRowHtml = gstRegistered && parseFloat(taxAmount) > 0 ? `
          <tr>
            <td style="padding: 12px 0; border-bottom: 1px solid #e8e6e1; color: #666;">GST (${gstRate}%)</td>
            <td style="padding: 12px 0; border-bottom: 1px solid #e8e6e1; text-align: right; color: #666;">$${taxAmount}</td>
          </tr>` : '';

    return `
      <!-- Invoice Section -->
      <div style="border-top: 2px solid #8a9957; padding-top: 24px; margin-top: 24px;">
        <h3 style="margin: 0 0 20px; color: #2d3319; font-size: 20px;">
          Invoice #${invoiceNumber}
        </h3>

        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          ${lineItemsHtml}
          ${gstRowHtml}
          <tr>
            <td style="padding: 16px 0; font-weight: bold; font-size: 18px; color: #2d3319;">Total Due</td>
            <td style="padding: 16px 0; text-align: right; font-weight: bold; font-size: 20px; color: #8a9957;">$${total}</td>
          </tr>
        </table>

        <p style="margin: 0 0 16px; color: #666;"><strong>Due Date:</strong> ${dueDateStr}</p>

        <!-- Bank Details -->
        <div style="background-color: #f0f4e8; padding: 20px; border-radius: 8px; margin-bottom: 16px;">
          <h4 style="margin: 0 0 12px; color: #2d3319; font-size: 16px;">Payment Details (Direct Debit)</h4>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 6px 0; color: #666; width: 140px;">Bank:</td>
              <td style="padding: 6px 0; color: #333; font-weight: 500;">${bankName}</td>
            </tr>
            <tr>
              <td style="padding: 6px 0; color: #666;">BSB:</td>
              <td style="padding: 6px 0; color: #333; font-weight: 500;">${bsb}</td>
            </tr>
            <tr>
              <td style="padding: 6px 0; color: #666;">Account Number:</td>
              <td style="padding: 6px 0; color: #333; font-weight: 500;">${accountNumber}</td>
            </tr>
            <tr>
              <td style="padding: 6px 0; color: #666;">Account Name:</td>
              <td style="padding: 6px 0; color: #333; font-weight: 500;">${accountName}</td>
            </tr>
            <tr>
              <td style="padding: 6px 0; color: #666;">Reference:</td>
              <td style="padding: 6px 0; color: #333; font-weight: 500;">${invoiceNumber}</td>
            </tr>
          </table>
        </div>

        ${paymentInstructions ? `
        <p style="margin: 0; color: #666; font-size: 14px;">
          ${paymentInstructions}
        </p>
        ` : `
        <p style="margin: 0; color: #666; font-size: 14px;">
          Please use your invoice number as the payment reference.
        </p>
        `}
      </div>
    `;
  }

  /**
   * Generate contact confirmation HTML content
   */
  generateContactHTML(contact) {
    const customerName = contact.name || 'there';
    const subject = contact.subject || 'Your Inquiry';
    const message = contact.message || '';

    return `
      <h2 style="margin: 0 0 24px; color: #8a9957; font-size: 24px; font-weight: 600;">
        Thank You for Your Message
      </h2>

      <p style="margin: 0 0 20px;">Hi ${customerName},</p>

      <p style="margin: 0 0 24px;">
        Thank you for reaching out to KO Holistic Health. We've received your message and will get back to you within 24-48 hours.
      </p>

      <!-- Message Details Box -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">Your Message</h3>
        <p style="margin: 0 0 12px;"><strong>Subject:</strong> ${subject}</p>
        <p style="margin: 0 0 8px;"><strong>Message:</strong></p>
        <blockquote style="margin: 0; padding: 16px; background-color: #ffffff; border-left: 4px solid #8a9957; border-radius: 4px; color: #555; font-style: italic;">
          ${message.replace(/\n/g, '<br>')}
        </blockquote>
      </div>

      <p style="margin: 0 0 8px;">
        In the meantime, feel free to explore our services on the website or follow us on social media.
      </p>

      <p style="margin: 24px 0 0;">
        Warmly,<br>
        <strong style="color: #8a9957;">Kirstie</strong><br>
        KO Holistic Health
      </p>
    `;
  }

  /**
   * Generate admin notification HTML for booking
   */
  generateAdminBookingHTML(booking, invoice = null, isNewClient = false) {
    const customerName = booking.clientName || booking.name || 'Unknown';
    const customerEmail = booking.clientEmail || booking.email || '';
    const customerPhone = booking.clientPhone || booking.phone || '';
    const serviceName = booking.serviceName || booking.service || 'Service';
    const bookingDate = booking.dateString || booking.date || '';
    const bookingTime = booking.time || '';
    const duration = booking.duration || '';
    const notes = booking.notes || booking.message || '';
    const price = invoice?.total ? `$${invoice.total.toFixed(2)}` : (booking.price ? `$${booking.price}` : 'N/A');

    return `
      <h2 style="margin: 0 0 24px; color: #8a9957; font-size: 24px; font-weight: 600;">
        New Booking Received
      </h2>

      ${isNewClient ? `
      <div style="display: inline-block; background-color: #d4edda; color: #155724; padding: 6px 16px; border-radius: 4px; font-size: 14px; font-weight: 600; margin-bottom: 20px;">
        NEW CLIENT
      </div>
      ` : ''}

      <!-- Client Details -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">Client Details</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #666; width: 100px;"><strong>Name:</strong></td>
            <td style="padding: 8px 0; color: #333;">${customerName}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Email:</strong></td>
            <td style="padding: 8px 0;"><a href="mailto:${customerEmail}" style="color: #8a9957;">${customerEmail}</a></td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Phone:</strong></td>
            <td style="padding: 8px 0;"><a href="tel:${customerPhone}" style="color: #8a9957;">${customerPhone}</a></td>
          </tr>
        </table>
      </div>

      <!-- Booking Details -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">Booking Details</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #666; width: 100px;"><strong>Service:</strong></td>
            <td style="padding: 8px 0; color: #333;">${serviceName}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Date:</strong></td>
            <td style="padding: 8px 0; color: #333;">${bookingDate}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Time:</strong></td>
            <td style="padding: 8px 0; color: #333;">${bookingTime}</td>
          </tr>
          ${duration ? `
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Duration:</strong></td>
            <td style="padding: 8px 0; color: #333;">${duration}</td>
          </tr>
          ` : ''}
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Price:</strong></td>
            <td style="padding: 8px 0; color: #333; font-weight: 600;">${price}</td>
          </tr>
        </table>
        ${notes ? `
        <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #e8e6e1;">
          <p style="margin: 0 0 8px; color: #666;"><strong>Notes:</strong></p>
          <p style="margin: 0; color: #333;">${notes.replace(/\n/g, '<br>')}</p>
        </div>
        ` : ''}
      </div>

      ${invoice ? `
      <p style="margin: 0 0 16px; color: #666;">
        <strong>Invoice #${invoice.invoiceNumber}</strong> has been automatically created and sent to the client.
      </p>
      ` : ''}

      <p style="margin: 24px 0 0;">
        <a href="https://koadmin.netlify.app/bookings" style="display: inline-block; background-color: #8a9957; color: #ffffff; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600; margin-right: 12px;">
          View Bookings
        </a>
        <a href="https://koadmin.netlify.app/invoices" style="display: inline-block; background-color: #6c757d; color: #ffffff; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600;">
          View Invoices
        </a>
      </p>
    `;
  }

  /**
   * Generate admin notification HTML for contact
   */
  generateAdminContactHTML(contact) {
    const customerName = contact.name || 'Unknown';
    const customerEmail = contact.email || '';
    const customerPhone = contact.phone || 'Not provided';
    const subject = contact.subject || 'General Inquiry';
    const message = contact.message || '';

    return `
      <h2 style="margin: 0 0 24px; color: #8a9957; font-size: 24px; font-weight: 600;">
        New Contact Form Submission
      </h2>

      <!-- Contact Details -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">Contact Details</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #666; width: 100px;"><strong>Name:</strong></td>
            <td style="padding: 8px 0; color: #333;">${customerName}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Email:</strong></td>
            <td style="padding: 8px 0;"><a href="mailto:${customerEmail}" style="color: #8a9957;">${customerEmail}</a></td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Phone:</strong></td>
            <td style="padding: 8px 0; color: #333;">${customerPhone}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Subject:</strong></td>
            <td style="padding: 8px 0; color: #333;">${subject}</td>
          </tr>
        </table>
      </div>

      <!-- Message -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">Message</h3>
        <p style="margin: 0; color: #333; line-height: 1.6;">
          ${message.replace(/\n/g, '<br>')}
        </p>
      </div>

      <p style="margin: 0;">
        <a href="mailto:${customerEmail}?subject=Re: ${encodeURIComponent(subject)}" style="display: inline-block; background-color: #8a9957; color: #ffffff; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600;">
          Reply to ${customerName}
        </a>
      </p>
    `;
  }

  /**
   * Send email via EmailJS
   */
  async sendEmail(params) {
    try {
      const result = await emailjs.send(
        CONFIG.serviceId,
        CONFIG.templateId,
        params
      );

      return {
        success: true,
        result,
        messageId: result.text
      };
    } catch (error) {
      console.error('EmailJS send error:', error);
      return {
        success: false,
        error: error.text || error.message
      };
    }
  }

  /**
   * Send booking confirmation to customer
   */
  async sendBookingConfirmation(booking, invoice = null) {
    if (!this.initialized) {
      await this.init();
    }

    const customerEmail = booking.clientEmail || booking.email;
    const serviceName = booking.serviceName || booking.service || 'Your Session';

    if (!customerEmail) {
      console.error('No customer email provided for booking confirmation');
      return { success: false, error: 'No customer email' };
    }

    const bodyContent = this.generateBookingHTML(booking, invoice);
    const htmlContent = this.wrapInEmailTemplate(bodyContent);

    const subject = invoice
      ? `Booking Confirmation & Invoice - ${serviceName} | KO Holistic Health`
      : `Booking Confirmation - ${serviceName} | KO Holistic Health`;

    return this.sendEmail({
      email: customerEmail,
      name: 'KO Holistic Health',
      subject: subject,
      html_content: htmlContent
    });
  }

  /**
   * Send booking notification to admin (Kirstie)
   */
  async sendBookingNotificationToAdmin(booking, invoice = null, isNewClient = false) {
    if (!this.initialized) {
      await this.init();
    }

    const customerName = booking.clientName || booking.name || 'Customer';
    const serviceName = booking.serviceName || booking.service || 'Service';
    const adminEmail = this.getAdminEmail();

    const bodyContent = this.generateAdminBookingHTML(booking, invoice, isNewClient);
    const htmlContent = this.wrapInEmailTemplate(bodyContent);

    return this.sendEmail({
      email: adminEmail,
      name: 'KO Holistic Health',
      subject: `New Booking: ${serviceName} - ${customerName}`,
      html_content: htmlContent
    });
  }

  /**
   * Send contact confirmation to customer
   */
  async sendContactConfirmation(contact) {
    if (!this.initialized) {
      await this.init();
    }

    const customerEmail = contact.email;

    if (!customerEmail) {
      console.error('No customer email provided for contact confirmation');
      return { success: false, error: 'No customer email' };
    }

    const bodyContent = this.generateContactHTML(contact);
    const htmlContent = this.wrapInEmailTemplate(bodyContent);

    return this.sendEmail({
      email: customerEmail,
      name: 'KO Holistic Health',
      subject: 'Thank You for Contacting Us | KO Holistic Health',
      html_content: htmlContent
    });
  }

  /**
   * Generate event registration confirmation HTML for customer
   */
  generateEventRegistrationHTML(event, attendee) {
    const customerName = attendee.name || 'there';
    const eventTitle = event.title || 'Event';
    const eventDate = event.date instanceof Date
      ? event.date.toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
      : event.date || '';
    const eventTime = event.time || '';
    const eventEndTime = event.endTime || '';
    const eventLocation = event.location || 'TBA';
    const eventPrice = event.price !== undefined ? `$${event.price}` : 'TBA';

    let content = `
      <h2 style="margin: 0 0 24px; color: #8a9957; font-size: 24px; font-weight: 600;">
        Event Registration Confirmed
      </h2>

      <p style="margin: 0 0 20px;">Hi ${customerName},</p>

      <p style="margin: 0 0 24px;">
        Thank you for registering! Your spot has been reserved for the following event:
      </p>

      <!-- Event Details Box -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">${eventTitle}</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #666; width: 120px;"><strong>Date:</strong></td>
            <td style="padding: 8px 0; color: #333;">${eventDate}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Time:</strong></td>
            <td style="padding: 8px 0; color: #333;">${eventTime}${eventEndTime ? ` - ${eventEndTime}` : ''}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Location:</strong></td>
            <td style="padding: 8px 0; color: #333;">${eventLocation}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Price:</strong></td>
            <td style="padding: 8px 0; color: #333;">${eventPrice}</td>
          </tr>
        </table>
      </div>
    `;

    if (event.whatToBring) {
      content += `
      <div style="background-color: #f0f4e8; padding: 20px; border-radius: 8px; margin-bottom: 24px;">
        <h4 style="margin: 0 0 12px; color: #2d3319; font-size: 16px;">What to Bring</h4>
        <p style="margin: 0; color: #333;">${event.whatToBring}</p>
      </div>
      `;
    }

    content += `
      <p style="margin: 0 0 8px;">If you have any questions or need to make changes to your registration, please don't hesitate to contact us.</p>

      <p style="margin: 24px 0 0;">
        Warmly,<br>
        <strong style="color: #8a9957;">Kirstie</strong><br>
        KO Holistic Health
      </p>
    `;

    return content;
  }

  /**
   * Generate admin notification HTML for event registration
   */
  generateAdminEventRegistrationHTML(event, attendee) {
    const customerName = attendee.name || 'Unknown';
    const customerEmail = attendee.email || '';
    const customerPhone = attendee.phone || 'Not provided';
    const eventTitle = event.title || 'Event';
    const eventDate = event.date instanceof Date
      ? event.date.toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
      : event.date || '';
    const eventTime = event.time || '';
    const notes = attendee.notes || '';
    const spotsRemaining = event.spots?.available !== undefined ? event.spots.available - 1 : 'N/A';

    return `
      <h2 style="margin: 0 0 24px; color: #8a9957; font-size: 24px; font-weight: 600;">
        New Event Registration
      </h2>

      <!-- Attendee Details -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">Attendee Details</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #666; width: 100px;"><strong>Name:</strong></td>
            <td style="padding: 8px 0; color: #333;">${customerName}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Email:</strong></td>
            <td style="padding: 8px 0;"><a href="mailto:${customerEmail}" style="color: #8a9957;">${customerEmail}</a></td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Phone:</strong></td>
            <td style="padding: 8px 0;"><a href="tel:${customerPhone}" style="color: #8a9957;">${customerPhone}</a></td>
          </tr>
        </table>
      </div>

      <!-- Event Details -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">Event Details</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #666; width: 100px;"><strong>Event:</strong></td>
            <td style="padding: 8px 0; color: #333;">${eventTitle}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Date:</strong></td>
            <td style="padding: 8px 0; color: #333;">${eventDate}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Time:</strong></td>
            <td style="padding: 8px 0; color: #333;">${eventTime}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Spots Left:</strong></td>
            <td style="padding: 8px 0; color: #333;">${spotsRemaining}</td>
          </tr>
        </table>
        ${notes ? `
        <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #e8e6e1;">
          <p style="margin: 0 0 8px; color: #666;"><strong>Notes:</strong></p>
          <p style="margin: 0; color: #333;">${notes.replace(/\n/g, '<br>')}</p>
        </div>
        ` : ''}
      </div>

      <p style="margin: 24px 0 0;">
        <a href="https://koadmin.netlify.app/events" style="display: inline-block; background-color: #8a9957; color: #ffffff; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600; margin-right: 12px;">
          View Events
        </a>
        <a href="mailto:${customerEmail}" style="display: inline-block; background-color: #6c757d; color: #ffffff; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600;">
          Email Attendee
        </a>
      </p>
    `;
  }

  /**
   * Send event registration confirmation to customer
   */
  async sendEventRegistrationConfirmation(event, attendee) {
    if (!this.initialized) {
      await this.init();
    }

    const customerEmail = attendee.email;

    if (!customerEmail) {
      console.error('No email provided for event registration confirmation');
      return { success: false, error: 'No customer email' };
    }

    const bodyContent = this.generateEventRegistrationHTML(event, attendee);
    const htmlContent = this.wrapInEmailTemplate(bodyContent, {
      preheader: `You're registered for ${event.title || 'our event'}!`
    });

    return this.sendEmail({
      email: customerEmail,
      name: 'KO Holistic Health',
      subject: `Event Registration Confirmed - ${event.title || 'Event'} | KO Holistic Health`,
      html_content: htmlContent
    });
  }

  /**
   * Send event registration notification to admin (Kirstie)
   */
  async sendEventRegistrationNotificationToAdmin(event, attendee) {
    if (!this.initialized) {
      await this.init();
    }

    const customerName = attendee.name || 'Attendee';
    const adminEmail = this.getAdminEmail();

    const bodyContent = this.generateAdminEventRegistrationHTML(event, attendee);
    const htmlContent = this.wrapInEmailTemplate(bodyContent);

    return this.sendEmail({
      email: adminEmail,
      name: 'KO Holistic Health',
      subject: `New Event Registration: ${event.title || 'Event'} - ${customerName}`,
      html_content: htmlContent
    });
  }

  /**
   * Generate course enrollment confirmation HTML for customer
   */
  generateCourseEnrollmentHTML(course, attendee) {
    const customerName = attendee.name || 'there';
    const courseTitle = course.title || 'Course';
    const courseType = course.type || 'course';
    const typeLabel = courseType === 'training' ? 'Training' : courseType === 'certification' ? 'Certification' : 'Course';
    const courseLocation = course.location || 'TBA';
    const coursePrice = course.price !== undefined ? (course.price === 0 ? 'Free' : `$${course.price} AUD`) : 'TBA';

    // Format session dates
    let scheduleSummary = 'Dates TBA';
    if (course.startDate || course.endDate) {
      const startStr = course.startDate ? new Date(course.startDate).toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'TBA';
      const endStr = course.endDate ? new Date(course.endDate).toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'TBA';
      scheduleSummary = `${startStr} — ${endStr}`;
    } else if (course.sessions?.length > 0) {
      const first = new Date(course.sessions[0].date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
      const last = new Date(course.sessions[course.sessions.length - 1].date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' });
      scheduleSummary = course.sessions.length === 1 ? first : `${first} - ${last}`;
    }

    let content = `
      <h2 style="margin: 0 0 24px; color: #8a9957; font-size: 24px; font-weight: 600;">
        Enrollment Confirmed
      </h2>

      <p style="margin: 0 0 20px;">Hi ${customerName},</p>

      <p style="margin: 0 0 24px;">
        Thank you for enrolling! Your spot has been reserved for the following ${typeLabel.toLowerCase()}:
      </p>

      <!-- Course Details Box -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">${courseTitle}</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #666; width: 120px;"><strong>Program:</strong></td>
            <td style="padding: 8px 0; color: #333;">${typeLabel}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Dates:</strong></td>
            <td style="padding: 8px 0; color: #333;">${scheduleSummary}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Sessions:</strong></td>
            <td style="padding: 8px 0; color: #333;">${course.sessions?.length || 0} session(s) · ${course.totalDuration || `${course.hoursTotal || 0} hours total`}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Location:</strong></td>
            <td style="padding: 8px 0; color: #333;">${courseLocation}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Investment:</strong></td>
            <td style="padding: 8px 0; color: #333; font-weight: 600;">${coursePrice}</td>
          </tr>
        </table>
      </div>
    `;

    // Session schedule
    if (course.sessions?.length > 0) {
      content += `
      <div style="background-color: #f0f4e8; padding: 20px; border-radius: 8px; margin-bottom: 24px;">
        <h4 style="margin: 0 0 12px; color: #2d3319; font-size: 16px;">Session Schedule</h4>
        <table style="width: 100%; border-collapse: collapse;">
          ${course.sessions.map((s, i) => `
          <tr>
            <td style="padding: 6px 0; color: #666; width: 130px;">${new Date(s.date).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
            <td style="padding: 6px 0; color: #333;">${s.startTime || ''} - ${s.endTime || ''}</td>
            <td style="padding: 6px 0; color: #333; font-weight: 500;">${s.title || `Session ${i + 1}`}</td>
          </tr>`).join('')}
        </table>
      </div>
      `;
    }

    if (course.certification) {
      content += `
      <div style="background-color: #fff8e1; padding: 20px; border-radius: 8px; margin-bottom: 24px; border-left: 4px solid #f9a825;">
        <h4 style="margin: 0 0 8px; color: #2d3319; font-size: 16px;">Certification</h4>
        <p style="margin: 0; color: #333;">Upon completion, you will receive: <strong>${course.certification.name || 'Certificate of Completion'}</strong>${course.certification.provider ? ` certified by ${course.certification.provider}` : ''}</p>
      </div>
      `;
    }

    if (course.whatToBring) {
      content += `
      <div style="background-color: #f0f4e8; padding: 20px; border-radius: 8px; margin-bottom: 24px;">
        <h4 style="margin: 0 0 12px; color: #2d3319; font-size: 16px;">What to Bring</h4>
        <p style="margin: 0; color: #333;">${course.whatToBring}</p>
      </div>
      `;
    }

    if (course.requirements) {
      content += `
      <div style="background-color: #f8f7f4; padding: 20px; border-radius: 8px; margin-bottom: 24px;">
        <h4 style="margin: 0 0 12px; color: #2d3319; font-size: 16px;">Prerequisites</h4>
        <p style="margin: 0; color: #333;">${course.requirements}</p>
      </div>
      `;
    }

    content += `
      <p style="margin: 0 0 8px;">If you have any questions or need to make changes to your enrollment, please don't hesitate to contact us.</p>

      <p style="margin: 24px 0 0;">
        Warmly,<br>
        <strong style="color: #8a9957;">Kirstie</strong><br>
        KO Holistic Health
      </p>
    `;

    return content;
  }

  /**
   * Generate admin notification HTML for course enrollment
   */
  generateAdminCourseEnrollmentHTML(course, attendee) {
    const customerName = attendee.name || 'Unknown';
    const customerEmail = attendee.email || '';
    const customerPhone = attendee.phone || 'Not provided';
    const courseTitle = course.title || 'Course';
    const courseType = course.type || 'course';
    const typeLabel = courseType === 'training' ? 'Training' : courseType === 'certification' ? 'Certification' : 'Course';
    const notes = attendee.notes || '';
    const spotsRemaining = course.spots?.available !== undefined ? course.spots.available - 1 : 'N/A';

    let scheduleSummary = 'Dates TBA';
    if (course.startDate || course.endDate) {
      const startStr = course.startDate ? new Date(course.startDate).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' }) : 'TBA';
      const endStr = course.endDate ? new Date(course.endDate).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'TBA';
      scheduleSummary = `${startStr} — ${endStr}`;
    } else if (course.sessions?.length > 0) {
      const first = new Date(course.sessions[0].date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
      const last = new Date(course.sessions[course.sessions.length - 1].date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' });
      scheduleSummary = course.sessions.length === 1 ? first : `${first} - ${last}`;
    }

    return `
      <h2 style="margin: 0 0 24px; color: #8a9957; font-size: 24px; font-weight: 600;">
        New ${typeLabel} Enrollment
      </h2>

      <!-- Attendee Details -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">Student Details</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #666; width: 100px;"><strong>Name:</strong></td>
            <td style="padding: 8px 0; color: #333;">${customerName}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Email:</strong></td>
            <td style="padding: 8px 0;"><a href="mailto:${customerEmail}" style="color: #8a9957;">${customerEmail}</a></td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Phone:</strong></td>
            <td style="padding: 8px 0;"><a href="tel:${customerPhone}" style="color: #8a9957;">${customerPhone}</a></td>
          </tr>
        </table>
      </div>

      <!-- Course Details -->
      <div style="background-color: #f8f7f4; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #2d3319; font-size: 18px;">Course Details</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #666; width: 100px;"><strong>Course:</strong></td>
            <td style="padding: 8px 0; color: #333;">${courseTitle}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Type:</strong></td>
            <td style="padding: 8px 0; color: #333;">${typeLabel}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Dates:</strong></td>
            <td style="padding: 8px 0; color: #333;">${scheduleSummary}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Sessions:</strong></td>
            <td style="padding: 8px 0; color: #333;">${course.sessions?.length || 0} session(s)</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Spots Left:</strong></td>
            <td style="padding: 8px 0; color: #333;">${spotsRemaining}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;"><strong>Price:</strong></td>
            <td style="padding: 8px 0; color: #333; font-weight: 600;">$${course.price || 0} AUD</td>
          </tr>
        </table>
        ${notes ? `
        <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #e8e6e1;">
          <p style="margin: 0 0 8px; color: #666;"><strong>Student Notes:</strong></p>
          <p style="margin: 0; color: #333;">${notes.replace(/\n/g, '<br>')}</p>
        </div>
        ` : ''}
      </div>

      <p style="margin: 24px 0 0;">
        <a href="https://koadmin.netlify.app/courses" style="display: inline-block; background-color: #8a9957; color: #ffffff; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600; margin-right: 12px;">
          View Courses
        </a>
        <a href="mailto:${customerEmail}" style="display: inline-block; background-color: #6c757d; color: #ffffff; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600;">
          Email Student
        </a>
      </p>
    `;
  }

  /**
   * Send course enrollment confirmation to customer
   */
  async sendCourseEnrollmentConfirmation(course, attendee) {
    if (!this.initialized) {
      await this.init();
    }

    const customerEmail = attendee.email;

    if (!customerEmail) {
      console.error('No email provided for course enrollment confirmation');
      return { success: false, error: 'No customer email' };
    }

    const bodyContent = this.generateCourseEnrollmentHTML(course, attendee);
    const htmlContent = this.wrapInEmailTemplate(bodyContent, {
      preheader: `You're enrolled in ${course.title || 'our course'}!`
    });

    return this.sendEmail({
      email: customerEmail,
      name: 'KO Holistic Health',
      subject: `Enrollment Confirmed - ${course.title || 'Course'} | KO Holistic Health`,
      html_content: htmlContent
    });
  }

  /**
   * Send course enrollment notification to admin (Kirstie)
   */
  async sendCourseEnrollmentNotificationToAdmin(course, attendee) {
    if (!this.initialized) {
      await this.init();
    }

    const customerName = attendee.name || 'Student';
    const adminEmail = this.getAdminEmail();

    const bodyContent = this.generateAdminCourseEnrollmentHTML(course, attendee);
    const htmlContent = this.wrapInEmailTemplate(bodyContent);

    return this.sendEmail({
      email: adminEmail,
      name: 'KO Holistic Health',
      subject: `New Enrollment: ${course.title || 'Course'} - ${customerName}`,
      html_content: htmlContent
    });
  }

  /**
   * Send contact notification to admin (Kirstie)
   */
  async sendContactNotificationToAdmin(contact) {
    if (!this.initialized) {
      await this.init();
    }

    const customerName = contact.name || 'Customer';
    const subject = contact.subject || 'General Inquiry';
    const adminEmail = this.getAdminEmail();

    const bodyContent = this.generateAdminContactHTML(contact);
    const htmlContent = this.wrapInEmailTemplate(bodyContent);

    return this.sendEmail({
      email: adminEmail,
      name: 'KO Holistic Health',
      subject: `New Contact: ${subject} - ${customerName}`,
      html_content: htmlContent
    });
  }
}

// Export singleton instance
export const emailService = new EmailService();
export default emailService;
